import { useState } from 'react';
import {
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@material-ui/core';
import {
  IconUser,
  IconChecks,
  IconMessageCircle,
  IconCalendarEvent,
  IconMapPin,
  IconStar,
  IconPackage,
  IconDotsVertical,
  IconTrash,
  IconClock,
  IconCopy,
  IconQuestionMark,
  IconArrowUp,
} from '@tabler/icons';
import { formatDistanceStrict } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';

import { useDialog } from '#root/hooks';
import { roundDecimal, replacePathParams } from '#root/utils';
import { ExpirationDays, ChatDrawer, ProvinceDialog } from '#root/components';
import { AlertDialog } from '#root/ui-components';
import { Badge, Button, Card } from '#root/components/ui/primitives';
import OfferStatusBadge from './OfferStatusBadge';
import { CanguroGuvery } from '#root/assets';
import { TRAVELER_DEAL_NEW } from '#root/constants/navigation';
import * as VARIABLES from '#root/constants/variables';
import styles from './offerItem.module.scss';

const PACKAGING_RANK: Record<string, number> = {
  'sin caja': 0,
  'caja abierta': 1,
  'sellado': 2,
};

const getTime = (date) => {
  try {
    const validDate = new Date(date.replace(/ /g, 'T'));
    const stringDate = validDate
      ? formatDistanceStrict(validDate, new Date(), { locale: es })
      : '---';
    return stringDate;
  } catch (error) {
    return '---';
  }
};

interface OfferItemProps {
  xref?: any;
  offer: any;
  fetchOfferReject?: (id) => void;
  fetchOfferExtend?: (id) => void;
}

const OfferItem = (props: OfferItemProps) => {
  const { xref, offer, fetchOfferReject, fetchOfferExtend } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState(false);
  const rejectDialog = useDialog();
  const extendDialog = useDialog();
  const chatDrawer = useDialog();
  const provinceDialog = useDialog();

  const handleCopyOrderId = async () => {
    try {
      await navigator.clipboard.writeText(String(offer.order.id_public));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // noop — clipboard puede estar bloqueado en contextos inseguros
    }
  };

  const order = offer.order;
  const shopper = offer.order.user;
  const offerCreateDate = getTime(offer.feccre);
  const isExpired = offer.is_expired === true;

  const orderRank = PACKAGING_RANK[order.empaque] ?? -1;
  const offerRank = PACKAGING_RANK[offer.empaque] ?? -1;
  const isOverDeliver =
    offer.empaque && order.empaque && offerRank > orderRank;
  const empaqueTooltip =
    offer.empaque && order.empaque
      ? `Pedido original: ${order.empaque}. Oferta propone: ${offer.empaque}.`
      : '';

  const isCreated = offer.est === '0';
  const isProcessing = offer.est === '1';
  const isAccepted = offer.est === '2';
  const isRejected =
    offer.est === '4' || offer.est === '5' || offer.est === '6' || offer.est === '7';

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openRejectAlert = () => {
    handleMenuClose();
    rejectDialog.openDialog();
  };
  const openExtendAlert = () => {
    handleMenuClose();
    extendDialog.openDialog();
  };

  const handleRejecteOffer = () => {
    fetchOfferReject(offer.ido);
  };

  const handleExtendOffer = () => {
    fetchOfferExtend(offer.ido);
  };

  return (
    <Card noBorder noHover>
      <div ref={xref} className={styles.root}>
        <div className={styles.cover}>
          {order?.img_path ? (
            <Avatar src={order.img_path} alt="Oferta">
              <img className={styles.placeholder} src={CanguroGuvery} alt="Pedido" />
            </Avatar>
          ) : (
            <img className={styles.placeholder} src={CanguroGuvery} alt="Pedido" />
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.flex}>
            <div className={styles.orderInfo}>
              <div className={styles.title}>
                <span className={styles.date}>Hace {offerCreateDate}</span>
                <span className={styles.separator}>·</span>
                <h4 className={styles.id}>Pedido #{offer.order.id_public}</h4>
                <Tooltip
                  arrow
                  title={copied ? 'Copiado' : 'Copiar número de pedido'}
                  placement="top">
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={handleCopyOrderId}
                    aria-label="Copiar número de pedido">
                    {copied ? <IconChecks size={16} /> : <IconCopy size={16} />}
                  </button>
                </Tooltip>
                {order.flag_envio === 1 && (
                  <Badge
                    variant="shipping"
                    size="sm"
                    onClick={provinceDialog.openDialog}
                    icon={<IconQuestionMark size={14} />}>
                    Envío Nacional
                  </Badge>
                )}
              </div>

              <Tooltip arrow title={order.tit}>
                <a
                  className={styles.nameLink}
                  href={order.url}
                  target="_blank"
                  rel="noopener noreferrer">
                  <h4 className={styles.name}>{order.tit}</h4>
                </a>
              </Tooltip>
              {order?.obs && (
                <div className={styles.obs}>
                  <p>{order.obs}</p>
                </div>
              )}
            </div>
            <div className={styles.metaRow}>
              <div className={styles.chips}>
                <Badge variant="outline" size="sm">
                  <a
                    className={styles.chipLink}
                    href={order.url}
                    target="_blank"
                    rel="noopener noreferrer">
                    {order.url_host}
                  </a>
                </Badge>
                {offer.empaque && (
                  <Tooltip arrow title={empaqueTooltip} placement="top">
                    <span className={styles.empaqueChipWrap}>
                      <Badge
                        variant="outline"
                        size="sm"
                        icon={<IconPackage size={14} />}
                        className={clsx(
                          styles.chipEmphasis,
                          isOverDeliver && styles.chipOverDeliver,
                        )}>
                        {offer.empaque}
                        {isOverDeliver && (
                          <IconArrowUp
                            size={12}
                            className={styles.overDeliverIcon}
                            aria-label="Mejor que lo pedido"
                          />
                        )}
                      </Badge>
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className={styles.priceInfo}>
                <div className={styles.price}>
                  <span className={styles.hint}>Cant.</span>
                  <span className={styles.number}>x {offer?.can}</span>
                </div>
                <span className={styles.priceSep} aria-hidden="true">·</span>
                <div className={styles.price}>
                  <span className={styles.hint}>Precio Real</span>
                  <span className={styles.number}>$ {roundDecimal(offer?.prerea)}</span>
                </div>
                <span className={styles.priceSep} aria-hidden="true">·</span>
                <div className={clsx(styles.price, styles.priceEarning)}>
                  <span className={styles.hint}>Ganancia</span>
                  <span className={styles.number}>$ {roundDecimal(offer?.comvia)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <div className={styles.flex}>
            <div className={styles.offerInfo}>
              {!isRejected && (
                <div className={styles.tools}>
                  <IconButton
                    color="secondary"
                    onClick={handleMenuClick}
                    className={styles.toolsButton}>
                    <IconDotsVertical size={22} />
                  </IconButton>
                  <Menu
                    id="tools-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}>
                    {offer?.fixed_expiration_days !== undefined && offer?.fixed_expiration_days < 1 &&
                      (
                        <MenuItem onClick={openExtendAlert}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <IconClock size={20} color="var(--color-status-success)" />
                          </ListItemIcon>
                          <h6>Extender vigencia</h6>
                        </MenuItem>
                      )
                    }
                    <MenuItem onClick={openRejectAlert}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <IconTrash size={20} color="var(--color-status-error)" />
                      </ListItemIcon>
                      <h6>Anular oferta</h6>
                    </MenuItem>
                    
                    
                    {(isProcessing || isAccepted) && (
                      <MenuItem
                        component="a"
                        href={VARIABLES.URL_PHP_PAGE + 'chat/oferta/' + offer.ido}>
                        <ListItemIcon style={{ minWidth: 32 }}>
                          <IconMessageCircle size={20} color="var(--color-graphite)" />
                        </ListItemIcon>
                        <h6>Ver Chat</h6>
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              )}
              <div className={styles.items}>
                <IconCalendarEvent size={20} />
                <div className={styles.info}>
                  <h6>{offer.fixed_delivery_date}</h6>
                </div>
              </div>
              <div className={styles.items}>
                <IconMapPin size={20} />
                <div className={styles.info}>
                  <h6>{offer.lugent}</h6>
                </div>
              </div>
            </div>
            <div className={styles.actionBlock}>
              {!isCreated && offer.status_label && (
                <OfferStatusBadge est={offer.est} label={offer.status_label} />
              )}
              {isCreated && !isExpired && (
                <div className={clsx(styles.alert, styles.created)}>
                  <h6>Esperando respuesta del comprador...</h6>
                </div>
              )}
              {isProcessing && !isExpired && (
                <div className={clsx(styles.alert, styles.processing)}>
                  <h6>
                    Si el comprador acepta la oferta, podrás crear un acuerdo respetando lo
                    conversado.
                  </h6>
                </div>
              )}
              {isAccepted && !isExpired && (
                <div className={clsx(styles.alert)}>
                  <h6>Oferta aceptada. Debes crear el Acuerdo.</h6>
                </div>
              )}
              {isAccepted && isExpired && (
                <div className={clsx(styles.alert)}>
                  <h6>Extiende la vigencia para crear el acuerdo.</h6>
                </div>
              )}
              {isAccepted && !isExpired && (
                <Button
                  variant="primary"
                  to={replacePathParams(TRAVELER_DEAL_NEW, { id: offer?.ido })}
                  icon={<IconChecks size={22} />}
                  style={{ width: '100%' }}>
                  Crear Acuerdo
                </Button>
              )}
              {!isRejected && (offer?.fixed_expiration_days !== undefined) && <ExpirationDays expirationDays={offer?.fixed_expiration_days} />}
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx(
          styles.footer,
          isCreated && styles.created,
          isProcessing && styles.processing,
          isRejected && styles.reject,
        )}>
        <div className={styles.userInfo}>
          <div className={styles.badge}>
            <Avatar alt={shopper?.mask_fullname} src={shopper?.picture_path}>
              <IconUser size={20} />
            </Avatar>
            <div className={styles.data}>
              <h6 className={clsx(styles.name, 'guv-ellipsis')}>{shopper?.mask_fullname}</h6>
              {shopper?.calcom && (
                <Tooltip arrow title="Puntaje del viajero" placement="top">
                  <div className={styles.userScore}>
                    <IconStar />
                    <span>{shopper.calcom}</span>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <div style={{ padding: '0 16px' }}>
          {(isProcessing || isAccepted) && (
            <Button
              variant="outline"
              onClick={chatDrawer.openDialog}
              icon={<IconMessageCircle size={22} />}>
              Ver Chat
            </Button>
          )}
        </div>
        {offer?.ido && (
          <ChatDrawer
            extraTitle={`Pedido #${offer?.order?.id_public}`}
            offerId={offer?.ido}
            open={chatDrawer.open}
            onClose={chatDrawer.closeDialog}
          />
        )}
      </div>
      <AlertDialog
        variant="error"
        open={rejectDialog.open}
        onClose={rejectDialog.closeDialog}
        title="Anular la oferta"
        message={
          <span>
            <span style={{ display: 'block' }}>
              ¿Estás seguro de que deseas anular esta oferta?
            </span>
            <span style={{ display: 'block' }}>Esta acción no se puede deshacer.</span>
          </span>
        }
        onConfirm={handleRejecteOffer}
        confirmText="Anular oferta"
      />
      <AlertDialog
        variant="info"
        open={extendDialog.open}
        onClose={extendDialog.closeDialog}
        title="Extender la vigencia"
        message={
          <span>
            <span style={{ display: 'block' }}>
              ¿Quieres extender la vigencia de esta oferta hasta mañana?
            </span>
          </span>
        }
        onConfirm={handleExtendOffer}
        confirmText="Extender vigencia"
      />
      <ProvinceDialog open={provinceDialog.open} onClose={provinceDialog.closeDialog} />
    </Card>
  );
};

export default OfferItem;
